import {
  addLUTData,
  AssetImporter,
  Box3,
  CameraView,
  CameraViewPlugin,
  DiamondMaterial,
  DiamondPlugin,
  GroundPlugin,
  IMaterial,
  Material,
  Object3D,
  Object3DModel,
  OrbitControls3,
  Sphere,
  TCameraControls,
  Vector3,
  ViewerApp,
} from "webgi";
import { BoundView } from "../types";
import getAngleData, {
  AngleDataType,
  CameraAngleType,
  CameraViewType,
  NumberVector,
} from "./getAngleData";
import { getDistance } from "./getDistance";

const gemShapes = [
  "round",
  "emerald",
  "cushion",
  "asscher",
  "heart",
  "marquise",
  "oval",
  "pear",
  "princess",
  "baguette",
  "half_moon",
  "halfmoon",
  "trapezoid",
  "trillion",
];

export const initCameraParams = (
  viewer: ViewerApp,
  camId: string,
  camAngle: CameraAngleType
) => {
  // Set initial camera positions
  const controls = viewer.scene.activeCamera.controls as OrbitControls3;
  const angleData = getAngleData(camId)!;
  const {
    minDistance,
    maxDistance,
    defaultView,
    minPolarAngle,
    maxPolarAngle,
  } = angleData;

  const camAngleData = (angleData[camAngle] || defaultView) as CameraViewType;
  const { camPosition, camTarget } = camAngleData;
  let position;
  let offsetNumber = 0;
  if (
    camId === "5" &&
    ["imageViewOne", "imageViewTwo", "camViewOne", "camViewTwo"].includes(
      camAngle
    )
  ) {
    position = camPosition;
    offsetNumber = -1;
  } else {
    const boundView = getBoundView(viewer, camPosition, camId);
    offsetNumber = boundView.offsetNumber;
    position = boundView.position;
  }
  if (camId === "1" && camAngle === "imageViewTwo") {
    offsetNumber = 1.325;
    position = { x: -7.775167081841214, y: 0, z: 0 };
  }
  // let offsetNumber = 0;
  controls.minDistance = minDistance;
  if (camAngle === "defaultView") {
    controls.minDistance = 0;
  }
  controls.maxDistance = maxDistance + offsetNumber;
  if (minPolarAngle) {
    controls.minPolarAngle = minPolarAngle;
  } else {
    controls.minPolarAngle = 0;
  }
  if (maxPolarAngle) {
    controls.maxPolarAngle = maxPolarAngle;
  } else {
    controls.maxPolarAngle = 3.14;
  }
  // Set active camera position and target
  // console.log("position", position);

  viewer.scene.activeCamera.position.set(position.x, position.y, position.z);
  if (camTarget) {
    viewer.scene.activeCamera.target.set(camTarget.x, camTarget.y, camTarget.z);

    if (camId === "5" && ["camViewOne", "imageViewTwo"].includes(camAngle)) {
      viewer.scene.activeCamera.targetUpdated();
    }
  }
  viewer.scene.activeCamera.positionUpdated();
  // viewer.scene.activeCamera.positionTargetUpdated();
  // if (camId === "5") {
  //   viewer.fitToView(viewer.scene.modelRoot, 1, 0);
  // }
};

export const getBoundView = (
  viewer: ViewerApp,
  camPosition: NumberVector,
  camId: string
): BoundView => {
  // Animate camera to new angle
  const camViewPlugin = viewer.getPlugin(CameraViewPlugin)!;
  let view = camViewPlugin.getCurrentCameraView(viewer.scene.activeCamera);
  view.position.set(camPosition.x, camPosition.y, camPosition.z);
  // Calculete the max dictance and apply it on max distance key
  const maxSize = Math.max(
    Math.abs(camPosition.x),
    Math.abs(camPosition.y),
    Math.abs(camPosition.z)
  );
  const maxIndex = view.position
    .toArray()
    .findIndex((num) => Math.abs(num) === maxSize);
  // Find the vector key with max distance
  const maxKeyItem: string = Object.keys(view.position)[maxIndex];
  const maxKey: "x" | "y" | "z" = maxKeyItem as "x" | "y" | "z";

  // Get distance based on model size
  const distance = getDistance(viewer, camId);

  // Calculte the distance required
  const offsetNumber = distance - Math.abs(view.position[maxKey]);
  view.position[maxKey] = distance * Math.sign(view.position[maxKey]);
  return {
    position: view.position,
    offsetNumber: offsetNumber,
    distance: distance,
  };
};

export const initModalParams = async (
  viewer: ViewerApp,
  camAngle: string,
  originalView: CameraView
) => {
  // Get required plugin
  const camViewPlugin = viewer.getPlugin(CameraViewPlugin)!;
  const groundPlugin = viewer.getPlugin(GroundPlugin);

  // Get angle data
  const { position, rotation, scale } = getAngleData(camAngle);

  // Set root modal position, rotation and scale
  if (typeof position !== "undefined") {
    viewer.scene.modelRoot.children[0].position.setY(position.y);
  }
  if (rotation?.x && rotation?.y && rotation?.z) {
    viewer.scene.modelRoot.children[0].rotation.x = rotation?.x;
    viewer.scene.modelRoot.children[0].rotation.y = rotation?.y;
    viewer.scene.modelRoot.children[0].rotation.z = rotation?.z;
  }
  viewer.scene.modelRoot.children[0].scale.setX(scale.x);
  viewer.scene.modelRoot.children[0].scale.setY(scale.y);
  viewer.scene.modelRoot.children[0].scale.setZ(scale.y);

  // Set original camera position
  originalView = camViewPlugin.getCurrentCameraView(viewer.scene.activeCamera);

  // Refresh ground
  groundPlugin?.refreshTransform();
};

export const initModelMaterials = async (
  viewer: ViewerApp,
  metalColor: string
) => {
  // Get required plugins
  const diamondManager = viewer.getPlugin(DiamondPlugin)!;
  const assetImporter = viewer.getManager()!.importer as AssetImporter;

  let promises: Promise<any>[] = [];
  let fn = async (m: Object3D) => {
    // Return if not a mesh
    if (!(<THREE.Mesh>m).isMesh) return;

    // Get mesh name and material name
    const meshName = m.name.toLowerCase() as string;
    const meshMaterial = (<THREE.Mesh>m).material as Material;
    const materialName = meshMaterial.name.toLowerCase();

    if (["hand", "hand_left", "nail"].includes(meshName)) return;
    if (meshName.includes("nail")) return;

    // Apply material based on shape
    var matAssigned: boolean;
    matAssigned = false;
    let gemShape = gemShapes.find((elm) => meshName.includes(elm)) || "";
    let gemShapeMat = gemShapes.find((elm) => materialName.includes(elm)) || "";
    if (gemShape != "" || gemShapeMat != "") {
      let boundBox = new Box3().setFromObject(m);
      let sizeKey = Math.round(boundBox.getSize(new Vector3()).x * 100) / 100;

      diamondManager.prepareDiamondMesh(<THREE.Mesh>m, {
        cacheKey: (gemShape || gemShapeMat) + sizeKey,
        normalMapRes: 512,
      });

      // Get diamond material based on the shape
      const dMat = await assetImporter.importSinglePath(
        `/assets/materials/diamonds/${gemShape || gemShapeMat}.dmat`
      );
      if (dMat) {
        const dMatMaterial: IMaterial = dMat as IMaterial;
        // Assign the material
        // @ts-ignore
        m.setMaterial(dMatMaterial);
        matAssigned = true;
      }
    }
    if (matAssigned == false) {
      const metalMat = await assetImporter.importSinglePath(
        `/assets/materials/metal/${metalColor}.pmat`
      );
      if (metalMat) {
        const metalMatMaterial: IMaterial = metalMat as IMaterial;
        // Assign the material
        // @ts-ignore
        m.setMaterial(metalMatMaterial);
        m.name = "metal";
      }
    }
    m.castShadow = true;
  };
  // --- Loop through all objects in scene ---
  viewer.scene.modelRoot.traverse((m) => {
    promises.push(fn(m));
  });
  await Promise.all(promises);
};

export const changeMetalColor = (viewer: ViewerApp, metalColor: string) => {
  return new Promise(async function (resolve, reject) {
    // Get required plugins
    const assetImporter = viewer.getManager()!.importer as AssetImporter;

    // --- Loop through all objects in scene ---
    let promises: Promise<any>[] = [];
    let fn = async (m: Object3D) => {
      // Return if not a mesh
      if (!(<THREE.Mesh>m).isMesh) return;

      // Get mesh name and material name
      const meshName = m.name.toLowerCase();
      const materialNameData = (<THREE.Mesh>m).material as DiamondMaterial;
      const materialName = materialNameData.name.toLowerCase();

      if (
        !materialNameData.isDiamondMaterial &&
        m.name !== "hand_left" &&
        !m.name.includes("nail_natural")
      ) {
        const metalMat = await assetImporter.importSinglePath(
          `/assets/materials/metal/${metalColor}.pmat`
        );

        // Assign the material
        // @ts-ignore
        m.setMaterial(metalMat);
      }
      m.castShadow = true;
    };
    // --- Loop through all objects in scene ---
    viewer.scene.modelRoot.traverse((m) => {
      promises.push(fn(m));
    });
    await Promise.all(promises);
    resolve(true);
  });
};

export const changeHandColor = (
  viewer: ViewerApp,
  handColor: string,
  camId: string
) => {
  return new Promise(async function (resolve, reject) {
    // Get required plugins
    const assetImporter = viewer.getManager()!.importer as AssetImporter;

    // --- Loop through all objects in scene ---
    let promises: Promise<any>[] = [];
    let fn = async (m: Object3D) => {
      // Return if not a mesh
      if (!(<THREE.Mesh>m).isMesh) return;

      if (m.name === "hand_left") {
        let handMat;
        if (camId.toString() === "1") {
          handMat = await assetImporter.importSinglePath(
            `/assets/materials/hand/config_1/${handColor}.pmat`
          );
        } else {
          handMat = await assetImporter.importSinglePath(
            `/assets/materials/hand/${handColor}.pmat`
          );
        }
        // Disable LUT on material
        // @ts-ignore
        addLUTData(handMat);
        // @ts-ignore
        handMat?.materialObject?.userData["LUTPlugin1"].enable = false;
        // Assign the material
        // @ts-ignore
        m.setMaterial(handMat);
      } else if (m.name.includes("nail")) {
        const naillMat = await assetImporter.importSinglePath(
          `/assets/materials/hand/config_1/nail.pmat`
        );
        // @ts-ignore
        m.setMaterial(naillMat);
      }
      m.castShadow = true;
    };
    viewer.scene.modelRoot.traverse((m) => {
      promises.push(fn(m));
    });
    await Promise.all(promises);
    resolve(true);
  });
};
